import React, { useRef, useState, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import './BannerImageSection.scss'

const createOptimizedBannerPreview = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
      const targetWidth = 1200
      const targetHeight = 400
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')

      canvas.width = targetWidth
      canvas.height = targetHeight

      if (!context) {
        URL.revokeObjectURL(objectUrl)
        reject(new Error('Unable to initialize banner preview'))
        return
      }

      context.fillStyle = '#e0f5f5'
      context.fillRect(0, 0, targetWidth, targetHeight)

      const coverScale = Math.max(targetWidth / image.width, targetHeight / image.height)
      const coverWidth = image.width * coverScale
      const coverHeight = image.height * coverScale
      const coverX = (targetWidth - coverWidth) / 2
      const coverY = (targetHeight - coverHeight) / 2

      context.save()
      context.globalAlpha = 0.18
      context.drawImage(image, coverX, coverY, coverWidth, coverHeight)
      context.restore()

      context.fillStyle = 'rgba(255, 255, 255, 0.3)'
      context.fillRect(0, 0, targetWidth, targetHeight)

      const containScale = Math.min(targetWidth / image.width, targetHeight / image.height) * 0.96
      const containWidth = image.width * containScale
      const containHeight = image.height * containScale
      const containX = (targetWidth - containWidth) / 2
      const containY = (targetHeight - containHeight) / 2

      context.drawImage(image, containX, containY, containWidth, containHeight)
      URL.revokeObjectURL(objectUrl)

      resolve(canvas.toDataURL(file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.92))
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Unable to load banner image'))
    }

    image.src = objectUrl
  })

const BannerImageSection = () => {
  const { watch, setValue } = useFormContext()
  const bannerImageUrl = watch('bannerImageUrl')

  const inputRef = useRef(null)
  const [localPreview, setLocalPreview] = useState('')
  const [bannerName, setBannerName] = useState('No file selected')
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)

  const bannerPreview = localPreview || bannerImageUrl

  useEffect(() => {
    if (!bannerImageUrl) {
      setLocalPreview('')
      setBannerName('No file selected')
    } else if (bannerImageUrl.startsWith('http') || bannerImageUrl.startsWith('data:image')) {
      setLocalPreview('')
      setBannerName('Loaded from settings')
    }
  }, [bannerImageUrl])

  const handleOpenUploader = () => {
    inputRef.current?.click()
  }

  const handleBannerChange = async (event) => {
    const { files } = event.target

    if (!files?.length) {
      return
    }

    const selectedFile = files[0]
    setBannerName(selectedFile.name)
    setValue('bannerFile', files)
    setIsGeneratingPreview(true)

    try {
      const optimizedPreview = await createOptimizedBannerPreview(selectedFile)
      setLocalPreview(optimizedPreview)
    } catch (_error) {
      setLocalPreview('')
      setBannerName('Preview unavailable')
    } finally {
      setIsGeneratingPreview(false)
    }
  }

  const handleRemoveBanner = (event) => {
    event.stopPropagation()
    setLocalPreview('')
    setBannerName('No file selected')
    setValue('bannerImageUrl', '')
    setValue('bannerFile', null)

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  return (
    <div className='settings-section'>
      <div className='settings-section-header'>
        <span className='settings-section-title'>Banner Images</span>
      </div>

      <div className='settings-section-body'>
        <button
          type='button'
          className={`settings-banner-dropzone ${bannerPreview ? 'has-image' : ''}`}
          onClick={handleOpenUploader}
        >
          {bannerPreview ? (
            <div className='settings-banner-stage'>
              <div
                className='settings-banner-backdrop'
                style={{ backgroundImage: `url(${bannerPreview})` }}
              />
              <div className='settings-banner-backdrop-tint' />
              <img src={bannerPreview} alt='Banner preview' className='settings-banner-image' />
            </div>
          ) : (
            <div className='settings-banner-placeholder'>
              <svg viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                <rect x='3' y='3' width='18' height='18' rx='2' />
                <circle cx='8.5' cy='8.5' r='1.5' />
                <polyline points='21 15 16 10 5 21' />
              </svg>
              <span className='settings-banner-placeholder-title'>
                {isGeneratingPreview ? 'Optimizing banner preview...' : 'Click to upload banner image'}
              </span>
              <span className='settings-banner-placeholder-subtitle'>
                {isGeneratingPreview
                  ? 'We are fitting your image into the banner frame for a cleaner preview.'
                  : 'PNG, JPG up to 5MB - recommended 1200 x 400px. Replaces the event page header.'}
              </span>
            </div>
          )}

          {bannerPreview && !isGeneratingPreview && (
            <div className='settings-banner-overlay'>
              <svg viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                <polyline points='17 8 12 3 7 8' />
                <line x1='12' y1='3' x2='12' y2='15' />
              </svg>
              <span>Change Banner</span>
            </div>
          )}
        </button>

        <input
          ref={inputRef}
          type='file'
          accept='image/*'
          hidden
          onChange={handleBannerChange}
        />

        <div className='settings-banner-footer'>
          <span className='settings-banner-filename'>{bannerName}</span>

          {bannerPreview && !isGeneratingPreview && (
            <button
              type='button'
              className='settings-banner-remove-btn'
              onClick={handleRemoveBanner}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default BannerImageSection
